import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import styled from 'styled-components'
import Papa from 'papaparse'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate } from 'react-router-dom'

import { Container } from '../../components/Animations'
import { IconLinkButton, PrimaryButton } from '../../components/Button'
import { InputLabel } from '../../components/Input'
import Modal from '../../components/Modal'
import Checkbox from '../../components/Checkbox'
import Tooltip from '../../components/Tooltip'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { SelectedMethodIcon } from '../../components/PaymentMethodDisplay'

import Upload from '../../icons/Upload'
import Download from '../../icons/Download'
import Close from '../../icons/Close'
import { Trash } from '../../icons/Trash'

import Treasury from '../../state/Treasury'
import User from '../../state/User'
import Radom, { CreateCryptoPayoutEntity } from '../../state/Radom'

import { ManagedPaymentMethod, getPaymentMethods } from '../../util/Managed'
import { RADOM_COLORS } from '../../util/Constants'
import { errorToast, formatCurrency, ROUTES, successToast, useTestMode } from '../../util/Util'

enum AccountType {
  Individual = 'Individual',
  Business = 'Business'
}

interface PayoutEntry {
  id: string
  amount: string
  payoutAddress: string
  name: string
  email: string
  accountType: AccountType
  dateOfBirth: string
  companyNumber: string
  residenceCountry: string
  address: string
}

const SUPPORTED_NETWORKS = ['Ethereum', 'SepoliaTestnet', 'Tron']
const SUPPORTED_TICKERS = ['ETH', 'USDT', 'USDC', 'TRX']

const networkLabel = (network: string): string => {
  switch (network) {
  case 'SepoliaTestnet':
    return 'Sepolia'
  default:
    return network
  }
}

const assetLabel = (method: ManagedPaymentMethod): string => method.currencyName || method.name

const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  border-bottom: 1px solid ${RADOM_COLORS.GRAY_MED};
  background: var(--surface, #ffffff);
  padding: 14px 20px;
`

const HeaderTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
`

const PageBody = styled.div`
  padding: 16px 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
  overflow-x: hidden;
  box-sizing: border-box;
`

const Toolbar = styled.div`
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-radius: 18px;
  background: var(--surface-subtle, #fcfcfd);
  padding: 16px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 14px;
  width: 100%;
  min-width: 0;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const ToolbarLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(360px, 100%);
  min-width: 0;
`

const SelectorLabel = styled.span`
  font-size: 12px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
`

const SelectorSelectedWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 30px;
`

const SelectorValueWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const SelectorPrimaryValue = styled.span`
  font-size: 13px;
  color: ${RADOM_COLORS.BLACK};
  font-weight: 500;
`

const SelectorSecondaryValue = styled.span`
  font-size: 11px;
  line-height: 1.2;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-style: italic;
`

const SelectorPlaceholder = styled.span`
  font-size: 13px;
  color: ${RADOM_COLORS.GRAY_DARKER};
`

const MethodOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const MethodOptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const MethodOptionTicker = styled.span`
  font-size: 13px;
  color: ${RADOM_COLORS.BLACK};
  font-weight: 500;
`

const MethodOptionNetwork = styled.span`
  font-size: 11px;
  line-height: 1.2;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-style: italic;
`

const ImportedNote = styled.span`
  font-size: 12px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
`

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-self: end;
  min-width: 0;

  @media (max-width: 980px) {
    width: 100%;
    justify-self: stretch;
    justify-content: flex-end;
  }
`

const TotalBadge = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-radius: 12px;
  padding: 10px 12px;
  background: var(--surface, #ffffff);
`

const TotalLabel = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${RADOM_COLORS.GRAY_DARKER};
`

const TotalValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${RADOM_COLORS.BLACK};
`

const TotalMeta = styled.span`
  font-size: 11px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
`

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 980px) {
    justify-content: flex-start;
  }
`

const HeaderButton = styled.button`
  border-radius: 12px;
  min-height: 40px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid var(--border-strong, #d6dae2);
  background: var(--surface, #ffffff);
  color: ${RADOM_COLORS.BLACK};
  cursor: pointer;
  transition: 0.16s ease background-color, 0.16s ease border-color, 0.16s ease opacity;

  &:hover {
    background: var(--surface-hover, #f7f7f8);
    border-color: var(--border-strong, #babfc8);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const SheetFrame = styled.div`
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-radius: 18px;
  background: var(--surface, #ffffff);
  overflow: hidden;
  max-height: calc(100vh - 300px);
  min-height: 180px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
`

const SheetScroller = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  scrollbar-gutter: stable both-edges;
  max-height: calc(100vh - 300px);
  width: 100%;
  min-width: 0;

  &::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: var(--surface-pressed, #eef1f7);
    border-radius: 20px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c2c9d8;
    border-radius: 20px;
  }
`

const SheetTable = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;

  tbody tr:not(.invalid-row):hover td {
    background: var(--surface-muted, #f8fafc);
  }

  tbody tr:not(.invalid-row):hover th {
    background: #f4f6f9;
  }

  tbody tr.inactive-row td {
    background: var(--surface-subtle, #fcfcfd);
  }

  tbody tr.inactive-row th {
    background: var(--surface-muted, #f8fafc);
    color: #99a3b7;
  }

  tbody tr.inactive-row input,
  tbody tr.inactive-row select {
    color: #93a0b9;
  }

  tbody tr.inactive-row input::placeholder {
    color: #aeb8cb;
  }
`

const CornerHeadCell = styled.th`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 4;
  width: 72px;
  min-width: 72px;
  background: var(--surface-hover, #f7f9fc);
  border-right: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-bottom: 1px solid ${RADOM_COLORS.GRAY_MED};
  font-size: 12px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  text-align: center;
  padding: 8px 0;
`

const HeadCell = styled.th`
  position: sticky;
  top: 0;
  z-index: 3;
  background: var(--surface-hover, #f7f9fc);
  border-right: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-bottom: 1px solid ${RADOM_COLORS.GRAY_MED};
  padding: 8px 10px;
  text-align: left;
  font-size: 12px;
  font-weight: 500;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  white-space: nowrap;
  vertical-align: bottom;
`

const HeaderMeta = styled.span`
  display: block;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${RADOM_COLORS.GRAY_DARKER};
  margin-bottom: 1px;
`

const HeaderTitle = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${RADOM_COLORS.GRAY_DARKEST};
`

const HeaderRequirement = styled.span<{ required: boolean }>`
  display: inline-block;
  margin-top: 3px;
  font-size: 10px;
  line-height: 1;
  color: ${({ required }): string => (required ? '#b12f2f' : '#8d96a9')};
  font-weight: 500;
`

const RowNumberCell = styled.th<{ invalid: boolean }>`
  position: sticky;
  left: 0;
  z-index: 2;
  width: 72px;
  min-width: 72px;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  border-right: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-bottom: 1px solid ${RADOM_COLORS.GRAY_MED};
  background: ${({ invalid }): string => (invalid ? '#fff2f2' : 'var(--surface-subtle, #fbfcff)')};
  box-shadow: ${({ invalid }): string => (invalid ? 'inset 3px 0 0 #e45757' : 'none')};
`

const RowNumberInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 0 6px;
  min-height: 42px;
`

const RowDeleteButton = styled.button`
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-radius: 6px;
  background: var(--surface, #ffffff);
  color: ${RADOM_COLORS.GRAY_DARKER};
  width: 24px;
  height: 24px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.95;

  &:hover {
    background: ${RADOM_COLORS.GRAY_LIGHTEST};
    color: ${RADOM_COLORS.BLACK};
  }
`

const Cell = styled.td<{ invalid: boolean }>`
  border-right: 1px solid ${RADOM_COLORS.GRAY_MED};
  border-bottom: 1px solid ${RADOM_COLORS.GRAY_MED};
  background: ${({ invalid }): string => (invalid ? '#fff7f7' : 'var(--surface, #ffffff)')};
  padding: 0;
  min-width: 140px;
`

const GridInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  padding: 9px 10px;
  font-size: 13px;
  color: ${RADOM_COLORS.BLACK};
  box-sizing: border-box;

  &:focus {
    background: var(--surface-muted, #f8fafc);
    box-shadow: inset 0 0 0 1px rgba(17, 17, 17, 0.16);
  }
`

const GridSelect = styled.select`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  padding: 9px 10px;
  font-size: 13px;
  color: ${RADOM_COLORS.BLACK};
  box-sizing: border-box;

  &:focus {
    background: var(--surface-muted, #f8fafc);
    box-shadow: inset 0 0 0 1px rgba(17, 17, 17, 0.16);
  }
`

const StatusText = styled.span<{ valid: boolean }>`
  display: inline-flex;
  width: 100%;
  padding: 9px 10px;
  font-size: 12px;
  color: ${({ valid }): string => (valid ? '#0b6b33' : '#b12f2f')};
  box-sizing: border-box;
`

const EmptySheetState = styled.div`
  border: 1px dashed var(--border-strong, #d6dae2);
  border-radius: 18px;
  background: var(--surface-subtle, #fcfcfd);
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`

const FooterNote = styled.span`
  font-size: 12px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
`

const newEntry = (): PayoutEntry => ({
  id: uuidv4(),
  amount: '',
  payoutAddress: '',
  name: '',
  email: '',
  accountType: AccountType.Individual,
  dateOfBirth: '',
  companyNumber: '',
  residenceCountry: '',
  address: ''
})

const normalizeAccountType = (value?: string): AccountType => {
  if (!value) return AccountType.Individual
  return value.trim().toLowerCase() === 'business' ? AccountType.Business : AccountType.Individual
}

const hasData = (entry: PayoutEntry): boolean => {
  return [
    entry.amount,
    entry.payoutAddress,
    entry.name,
    entry.email,
    entry.dateOfBirth,
    entry.companyNumber,
    entry.residenceCountry,
    entry.address
  ].some((value) => value.trim().length > 0)
}

const toSlashDate = (value?: string): string => {
  if (!value) return ''
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(value)) return value

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''

  const y = parsed.getFullYear()
  const m = `${parsed.getMonth() + 1}`.padStart(2, '0')
  const d = `${parsed.getDate()}`.padStart(2, '0')
  return `${y}/${m}/${d}`
}

const toInputDate = (value?: string): string => {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value

  const slash = value.match(/^(\d{4})\/(\d{2})\/(\d{2})$/)
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''

  const y = parsed.getFullYear()
  const m = `${parsed.getMonth() + 1}`.padStart(2, '0')
  const d = `${parsed.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}

const withSingleTrailingBlank = (rows: PayoutEntry[]): PayoutEntry[] => {
  const next = rows.length > 0 ? [...rows] : [newEntry()]

  while (next.length > 1 && !hasData(next[next.length - 1]) && !hasData(next[next.length - 2])) {
    next.pop()
  }

  if (hasData(next[next.length - 1])) {
    next.push(newEntry())
  }

  return next
}

const CreateMassivePayout = observer((): ReactElement => {
  const navigate = useNavigate()
  const [testMode] = useTestMode()

  const [selectedMethod, setSelectedMethod] = useState<ManagedPaymentMethod>()
  const [rows, setRows] = useState<PayoutEntry[]>([newEntry()])
  const [isLoading, setIsLoading] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [deductFees, setDeductFees] = useState(true)
  const [sendEmailReceipts, setSendEmailReceipts] = useState(false)
  const [importedFileName, setImportedFileName] = useState<string>()

  const uploadInputRef = useRef<HTMLInputElement | null>(null)
  const closeAssetDropdownRef = useRef<(() => void) | null>(null)
  const sheetScrollerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (User.hasPermission('ReadBalance')) {
      Treasury.loadBalances()
    }
    Treasury.loadPriceQuotes()
  }, [])

  useEffect(() => {
    const previousHtmlOverflowX = document.documentElement.style.overflowX
    const previousBodyOverflowX = document.body.style.overflowX

    document.documentElement.style.overflowX = 'hidden'
    document.body.style.overflowX = 'hidden'
    document.documentElement.scrollLeft = 0
    document.body.scrollLeft = 0
    window.scrollTo({ left: 0 })

    return () => {
      document.documentElement.style.overflowX = previousHtmlOverflowX
      document.body.style.overflowX = previousBodyOverflowX
    }
  }, [])

  const selectableMethods = useMemo(() => {
    const allowedByNetwork: Record<string, string[]> = {
      Ethereum: ['ETH', 'USDC', 'USDT'],
      SepoliaTestnet: ['ETH', 'USDC', 'USDT'],
      Tron: ['TRX', 'USDT']
    }

    const filtered = getPaymentMethods()
      .filter((method) => !method.isHidden)
      .filter((method) => !method.isDisabled)
      .filter((method) => {
        const network = method.hostChain?.name ?? method.name
        return SUPPORTED_NETWORKS.includes(network)
      })
      .filter((method) => SUPPORTED_TICKERS.includes(method.ticker))
      .filter((method) => method.ticker !== 'BAT')
      .filter((method) => {
        const network = method.hostChain?.name ?? method.name
        return (allowedByNetwork[network] || []).includes(method.ticker)
      })
      .filter((method) => testMode ? method.isTestnet : !method.isTestnet)

    const unique = new Map<string, ManagedPaymentMethod>()
    filtered.forEach((method) => {
      const network = method.hostChain?.name ?? method.name
      const token = method.hostChain?.tokenAddress ?? 'native'
      unique.set(`${network}:${token}`, method)
    })

    return [...unique.values()].sort((a, b) => {
      const aNetwork = a.hostChain?.name ?? a.name
      const bNetwork = b.hostChain?.name ?? b.name
      if (aNetwork === bNetwork) return a.ticker.localeCompare(b.ticker)
      return aNetwork.localeCompare(bNetwork)
    })
  }, [testMode])

  const sheetNetwork = selectedMethod?.hostChain?.name ?? selectedMethod?.name
  const sheetToken = selectedMethod?.hostChain?.tokenAddress

  useEffect(() => {
    sheetScrollerRef.current?.scrollTo({ left: 0 })
  }, [sheetNetwork])

  const activeRows = useMemo(() => rows.filter(hasData), [rows])

  const rowIssues = useMemo(() => {
    const map = new Map<string, string[]>()

    rows.forEach((row) => {
      if (!hasData(row)) {
        map.set(row.id, [])
        return
      }

      const issues: string[] = []
      const amount = Number(row.amount)

      if (!row.amount.trim() || Number.isNaN(amount) || amount <= 0) issues.push('amount')
      if (!row.payoutAddress.trim()) issues.push('payout address')
      if (!row.name.trim()) issues.push('beneficiary name')
      if (!row.email.trim()) issues.push('email')

      map.set(row.id, issues)
    })

    return map
  }, [rows])

  const invalidRowsCount = useMemo(
    () => activeRows.filter((row) => (rowIssues.get(row.id) || []).length > 0).length,
    [activeRows, rowIssues]
  )

  const totalUsd = useMemo(() => {
    const ticker = selectedMethod?.ticker?.toLowerCase()
    const quote = ticker
      ? Treasury.priceQuotes.find((item) => item.from.toLowerCase() === ticker)
      : undefined

    return activeRows.reduce((total, row) => {
      const amount = Number(row.amount)
      if (Number.isNaN(amount) || amount <= 0) return total
      return total + amount * (quote?.value || 1)
    }, 0)
  }, [activeRows, selectedMethod, Treasury.priceQuotes])

  const canCreate = activeRows.length > 0 && invalidRowsCount === 0 && !!sheetNetwork

  const updateRow = (id: string, patch: Partial<PayoutEntry>): void => {
    setRows((prev) => withSingleTrailingBlank(prev.map((row) => (row.id === id ? { ...row, ...patch } : row))))
  }

  const deleteRow = (index: number): void => {
    setRows((prev) => {
      const next = [...prev]
      next.splice(index, 1)
      return withSingleTrailingBlank(next)
    })
  }

  const applyCellValue = (row: PayoutEntry, colIndex: number, value: string): PayoutEntry => {
    const trimmed = value.trim()

    switch (colIndex) {
    case 0:
      return { ...row, amount: trimmed }
    case 1:
      return { ...row, payoutAddress: trimmed }
    case 2:
      return { ...row, name: trimmed }
    case 3:
      return { ...row, email: trimmed }
    case 4:
      return { ...row, accountType: normalizeAccountType(trimmed) }
    case 5:
      return row.accountType === AccountType.Business
        ? { ...row, companyNumber: trimmed }
        : { ...row, dateOfBirth: toSlashDate(trimmed) }
    case 6:
      return { ...row, residenceCountry: trimmed }
    case 7:
      return { ...row, address: trimmed }
    default:
      return row
    }
  }

  const applyPastedGrid = (text: string, startRow: number, startCol: number): void => {
    if (!sheetNetwork) {
      errorToast('Select a sheet asset before pasting rows')
      return
    }

    const rawRows = text
      .replace(/\r/g, '')
      .split('\n')
      .filter((line) => line.trim().length > 0)

    if (rawRows.length === 0) return

    setRows((prev) => {
      const next = [...prev]

      rawRows.forEach((line, rOff) => {
        const target = startRow + rOff

        while (target >= next.length) {
          next.push(newEntry())
        }

        const cells = line.split('\t')
        let patched = { ...next[target] }

        cells.forEach((cell, cOff) => {
          patched = applyCellValue(patched, startCol + cOff, cell)
        })

        next[target] = patched
      })

      return withSingleTrailingBlank(next)
    })
  }

  const onCellPaste = (
    event: React.ClipboardEvent<HTMLInputElement | HTMLSelectElement>,
    rowIndex: number,
    colIndex: number
  ): void => {
    const text = event.clipboardData.getData('text')
    if (!text) return

    if (!text.includes('\t') && !text.includes('\n')) {
      return
    }

    event.preventDefault()
    applyPastedGrid(text, rowIndex, colIndex)
  }

  const parseCsv = (file: File): void => {
    if (!sheetNetwork) {
      errorToast('Select a sheet asset before uploading a spreadsheet')
      return
    }

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed: PayoutEntry[] = []

        for (const row of results.data || []) {
          const network = row.network?.trim()
          if (network && !SUPPORTED_NETWORKS.includes(network)) {
            errorToast('Only Ethereum, SepoliaTestnet and Tron are currently supported for payouts')
            return
          }

          const hasAnyValue = Object.values(row).some((value) =>
            typeof value === 'string' && value.trim().length > 0
          )
          if (!hasAnyValue) continue

          parsed.push({
            id: uuidv4(),
            amount: row.amount?.trim() || '',
            payoutAddress: row.payout_address?.trim() || '',
            name: row.beneficiary_name?.trim() || '',
            email: row.email?.trim() || '',
            accountType: normalizeAccountType(row.account_type),
            dateOfBirth: toSlashDate(row['date_of_birth (INDIVIDUAL ONLY)']),
            companyNumber: row['company_number (BUSINESS ONLY)']?.trim() || '',
            residenceCountry: row.country_of_beneficiary?.trim() || '',
            address: row.beneficiary_address?.trim() || ''
          })
        }

        if (parsed.length === 0) {
          errorToast('No payout rows found in spreadsheet')
          return
        }

        setRows(withSingleTrailingBlank(parsed))
        setImportedFileName(file.name)
        successToast(`Imported ${parsed.length} row(s)`)
      },
      error: (error) => {
        console.error('CSV parse error:', error)
        errorToast('Failed to parse spreadsheet')
      }
    })
  }

  const onUploadChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (!file) return

    parseCsv(file)
    event.target.value = ''
  }

  const toCreateCryptoPayoutEntry = (row: PayoutEntry): CreateCryptoPayoutEntity => ({
    gateway: 'crypto' as const,
    amount: row.amount,
    payoutAddress: row.payoutAddress.trim(),
    network: sheetNetwork || '',
    token: sheetToken,
    accountType: row.accountType,
    email: row.email.trim() || undefined,
    name: row.name.trim() || undefined,
    dateOfBirth: row.dateOfBirth || undefined,
    countryOfResidence: row.residenceCountry || undefined,
    companyName: row.accountType === AccountType.Business ? (row.name.trim() || undefined) : undefined,
    addressLine1: row.address.trim() || undefined
  })

  const createPayout = async (): Promise<void> => {
    if (!sheetNetwork) {
      errorToast('Select a sheet asset before creating a payout')
      return
    }

    if (activeRows.length === 0) {
      errorToast('Add at least one payout row')
      return
    }

    if (invalidRowsCount > 0) {
      errorToast(`Resolve validation errors in ${invalidRowsCount} row(s)`)
      return
    }

    setIsLoading(true)
    try {
      await Radom.createPayout({
        deductFees,
        sendEmailReceipts,
        payouts: activeRows.map(toCreateCryptoPayoutEntry)
      })

      successToast('Mass payout created successfully')
      navigate(ROUTES.MASSIVE_PAYOUTS)
    } catch (err: any) {
      errorToast(err?.response?.data?.error || err?.message || 'Failed to create mass payout')
    } finally {
      setIsLoading(false)
    }
  }

  return <Container style={{
    padding: 0,
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box'
  }}>
    <HeaderBar>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <IconLinkButton to={ROUTES.MASSIVE_PAYOUTS} type='button' style={{ padding: 5 }}>
          <Close style={{ width: 15 }} />
        </IconLinkButton>

        <HeaderTitleWrap>
          <span style={{ fontSize: 18 }}>Create mass payout</span>
        </HeaderTitleWrap>
      </div>

      <HeaderActions>
        <HeaderButton type='button' onClick={() => uploadInputRef.current?.click()}>
          <Upload style={{ width: 16, height: 16 }} stroke={RADOM_COLORS.BLACK} />
          Upload
        </HeaderButton>

        <HeaderButton
          type='button'
          onClick={() => {
            window.open(
              'https://s3.us-east-1.amazonaws.com/test.radom.network/Radom+-+Mass+Crypto+Payouts+Template.csv',
              '_blank'
            )
          }}>
          <span>Download template</span>
          <Download />
        </HeaderButton>
      </HeaderActions>
    </HeaderBar>

    <PageBody>
      <Toolbar>
        <ToolbarLeft>
          <SelectorLabel>Asset</SelectorLabel>
          <Dropdown
            disabled={isLoading}
            preferredAlignment='left'
            onCloseFn={(fn) => { closeAssetDropdownRef.current = fn }}
            outerStyle={{ minHeight: 42, borderRadius: 8, padding: '6px 10px' }}
            selectedContent={
              <SelectorSelectedWrap>
                {selectedMethod
                  ? <>
                    <SelectedMethodIcon method={selectedMethod} />
                    <SelectorValueWrap>
                      <SelectorPrimaryValue>
                        {selectedMethod.ticker} · {assetLabel(selectedMethod)}
                      </SelectorPrimaryValue>
                      <SelectorSecondaryValue>
                        on {networkLabel(sheetNetwork || '')}
                      </SelectorSecondaryValue>
                    </SelectorValueWrap>
                  </>
                  : <SelectorPlaceholder>Select asset for all rows</SelectorPlaceholder>}
              </SelectorSelectedWrap>
            }
            dropdownContent={
              <div>
                {selectableMethods.length === 0 &&
                  <DropdownItem style={{ opacity: 0.7 }}>
                    No payout assets available
                  </DropdownItem>}

                {selectableMethods.map((method) => {
                  const network = method.hostChain?.name ?? method.name
                  const key = `${network}:${method.hostChain?.tokenAddress ?? 'native'}`
                  return <DropdownItem key={key} onClick={() => {
                    setSelectedMethod(method)
                    closeAssetDropdownRef.current?.()
                  }}>
                    <MethodOptionRow>
                      <SelectedMethodIcon method={method} />
                      <MethodOptionText>
                        <MethodOptionTicker>{method.ticker} · {assetLabel(method)}</MethodOptionTicker>
                        <MethodOptionNetwork>on {networkLabel(network)}</MethodOptionNetwork>
                      </MethodOptionText>
                    </MethodOptionRow>
                  </DropdownItem>
                })}
              </div>
            }
          />
          {importedFileName && <ImportedNote>Imported: {importedFileName}</ImportedNote>}
        </ToolbarLeft>

        <ToolbarRight>
          <TotalBadge>
            <TotalLabel>Total</TotalLabel>
            <TotalValue>{formatCurrency(totalUsd, User.organization?.defaultCurrency ?? 'USD')}</TotalValue>
            <TotalMeta>
              {activeRows.length} row(s){importedFileName ? ` · ${importedFileName}` : ''}
            </TotalMeta>
          </TotalBadge>

          <PrimaryButton
            style={{ minHeight: 44, padding: '10px 24px' }}
            disabled={!canCreate || isLoading}
            onClick={() => setIsCreateModalOpen(true)}>
            Create payout
          </PrimaryButton>
        </ToolbarRight>

        <input
          ref={uploadInputRef}
          type='file'
          accept='.csv'
          onChange={onUploadChange}
          style={{ display: 'none' }}
        />
      </Toolbar>

      {!sheetNetwork &&
        <EmptySheetState>
          <span style={{ fontSize: 18, fontWeight: 500 }}>Select a payout asset to start adding rows</span>
          <FooterNote>
            The selected payout asset will apply to every row in this batch.
          </FooterNote>
        </EmptySheetState>
      }

      {sheetNetwork &&
        <SheetFrame>
          <SheetScroller ref={sheetScrollerRef}>
            <SheetTable>
              <colgroup>
                <col style={{ width: '72px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '270px' }} />
                <col style={{ width: '190px' }} />
                <col style={{ width: '220px' }} />
                <col style={{ width: '150px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '170px' }} />
                <col style={{ width: '220px' }} />
                <col style={{ width: '220px' }} />
              </colgroup>

              <thead>
                <tr>
                  <CornerHeadCell>#</CornerHeadCell>
                  <HeadCell>
                    <HeaderMeta>A</HeaderMeta>
                    <HeaderTitle>Amount</HeaderTitle>
                    <HeaderRequirement required>Required</HeaderRequirement>
                  </HeadCell>
                  <HeadCell>
                    <HeaderMeta>B</HeaderMeta>
                    <HeaderTitle>Payout address</HeaderTitle>
                    <HeaderRequirement required>Required</HeaderRequirement>
                  </HeadCell>
                  <HeadCell>
                    <HeaderMeta>C</HeaderMeta>
                    <HeaderTitle>Beneficiary name</HeaderTitle>
                    <HeaderRequirement required>Required</HeaderRequirement>
                  </HeadCell>
                  <HeadCell>
                    <HeaderMeta>D</HeaderMeta>
                    <HeaderTitle>Email</HeaderTitle>
                    <HeaderRequirement required>Required</HeaderRequirement>
                  </HeadCell>
                  <HeadCell>
                    <HeaderMeta>E</HeaderMeta>
                    <HeaderTitle>Owner type</HeaderTitle>
                    <HeaderRequirement required={false}>Optional</HeaderRequirement>
                  </HeadCell>
                  <HeadCell>
                    <HeaderMeta>F</HeaderMeta>
                    <HeaderTitle>DOB / Company number</HeaderTitle>
                    <HeaderRequirement required={false}>Optional</HeaderRequirement>
                  </HeadCell>
                  <HeadCell>
                    <HeaderMeta>G</HeaderMeta>
                    <HeaderTitle>Residence</HeaderTitle>
                    <HeaderRequirement required={false}>Optional</HeaderRequirement>
                  </HeadCell>
                  <HeadCell>
                    <HeaderMeta>H</HeaderMeta>
                    <HeaderTitle>Address</HeaderTitle>
                    <HeaderRequirement required={false}>Optional</HeaderRequirement>
                  </HeadCell>
                  <HeadCell>
                    <HeaderMeta>I</HeaderMeta>
                    <HeaderTitle>Status</HeaderTitle>
                  </HeadCell>
                </tr>
              </thead>

              <tbody>
                {rows.map((row, rowIndex) => {
                  const issues = rowIssues.get(row.id) || []
                  const rowHasData = hasData(row)
                  const rowInvalid = rowHasData && issues.length > 0
                  const statusText = !rowHasData
                    ? ''
                    : (rowInvalid ? `Missing: ${issues.join(', ')}` : 'Ready')

                  const rowClass = rowInvalid ? 'invalid-row' : (!rowHasData ? 'inactive-row' : undefined)

                  return <tr key={row.id} className={rowClass}>
                    <RowNumberCell invalid={rowInvalid}>
                      <RowNumberInner>
                        <span>{rowIndex + 1}</span>
                        <RowDeleteButton title='Delete row' onClick={() => deleteRow(rowIndex)}>
                          <Trash width={12} height={12} stroke={RADOM_COLORS.GRAY_DARKER} />
                        </RowDeleteButton>
                      </RowNumberInner>
                    </RowNumberCell>

                    <Cell invalid={rowInvalid}>
                      <GridInput
                        value={row.amount}
                        onChange={(e) => updateRow(row.id, { amount: e.target.value })}
                        onPaste={(e) => onCellPaste(e, rowIndex, 0)}
                        placeholder='0.00'
                      />
                    </Cell>

                    <Cell invalid={rowInvalid}>
                      <GridInput
                        value={row.payoutAddress}
                        onChange={(e) => updateRow(row.id, { payoutAddress: e.target.value })}
                        onPaste={(e) => onCellPaste(e, rowIndex, 1)}
                        placeholder='Wallet address'
                      />
                    </Cell>

                    <Cell invalid={rowInvalid}>
                      <GridInput
                        value={row.name}
                        onChange={(e) => updateRow(row.id, { name: e.target.value })}
                        onPaste={(e) => onCellPaste(e, rowIndex, 2)}
                        placeholder='Full name'
                      />
                    </Cell>

                    <Cell invalid={rowInvalid}>
                      <GridInput
                        value={row.email}
                        onChange={(e) => updateRow(row.id, { email: e.target.value })}
                        onPaste={(e) => onCellPaste(e, rowIndex, 3)}
                        placeholder='email@example.com'
                      />
                    </Cell>

                    <Cell invalid={rowInvalid}>
                      <GridSelect
                        value={row.accountType}
                        onChange={(e) => updateRow(row.id, { accountType: normalizeAccountType(e.target.value) })}
                        onPaste={(e) => onCellPaste(e, rowIndex, 4)}>
                        <option value={AccountType.Individual}>Individual</option>
                        <option value={AccountType.Business}>Business</option>
                      </GridSelect>
                    </Cell>

                    <Cell invalid={rowInvalid}>
                      {row.accountType === AccountType.Individual
                        ? <GridInput
                          type='date'
                          value={toInputDate(row.dateOfBirth)}
                          onChange={(e) => updateRow(row.id, { dateOfBirth: toSlashDate(e.target.value) })}
                          onPaste={(e) => onCellPaste(e, rowIndex, 5)}
                        />
                        : <GridInput
                          value={row.companyNumber}
                          onChange={(e) => updateRow(row.id, { companyNumber: e.target.value })}
                          onPaste={(e) => onCellPaste(e, rowIndex, 5)}
                          placeholder='Company number'
                        />}
                    </Cell>

                    <Cell invalid={rowInvalid}>
                      <GridInput
                        value={row.residenceCountry}
                        onChange={(e) => updateRow(row.id, { residenceCountry: e.target.value })}
                        onPaste={(e) => onCellPaste(e, rowIndex, 6)}
                        placeholder='Country'
                      />
                    </Cell>

                    <Cell invalid={rowInvalid}>
                      <GridInput
                        value={row.address}
                        onChange={(e) => updateRow(row.id, { address: e.target.value })}
                        onPaste={(e) => onCellPaste(e, rowIndex, 7)}
                        placeholder='Address line'
                      />
                    </Cell>

                    <Cell invalid={rowInvalid}>
                      <StatusText valid={!rowInvalid}>{statusText}</StatusText>
                    </Cell>
                  </tr>
                })}
              </tbody>
            </SheetTable>
          </SheetScroller>
        </SheetFrame>
      }

      <FooterNote>
        Tip: copy rows from Excel/Google Sheets and paste directly into any sheet cell.
      </FooterNote>
    </PageBody>

    {isCreateModalOpen &&
      <Modal
        title='Review mass payout settings'
        visible={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        innerStyles={{ width: 560 }}>

        <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{
            border: `1px solid ${RADOM_COLORS.GRAY_MED}`,
            background: 'var(--surface-subtle, #f9fafb)',
            borderRadius: 16,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 6
          }}>
            <span style={{ color: 'var(--ink, #20242d)', fontWeight: 500 }}>{activeRows.length} payout row(s)</span>
            <span style={{ color: 'var(--text-muted, #5f6675)' }}>Estimated total: <span style={{ color: 'var(--ink, #20242d)', fontWeight: 500 }}>{formatCurrency(totalUsd, User.organization?.defaultCurrency ?? 'USD')}</span></span>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            padding: 16,
            borderRadius: 16,
            border: '1px solid var(--border-soft, #eceff3)',
            background: 'var(--surface-subtle, #fcfcfd)'
          }}>
            <InputLabel>Email receipts</InputLabel>
            <div
              onClick={() => setSendEmailReceipts(!sendEmailReceipts)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <Checkbox checked={sendEmailReceipts} disabled={isLoading} />
              <span>Send payout recipients an email receipt</span>
            </div>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            padding: 16,
            borderRadius: 16,
            border: '1px solid var(--border-soft, #eceff3)',
            background: 'var(--surface-subtle, #fcfcfd)'
          }}>
            <div style={{ display: 'flex', gap: 3 }}>
              <InputLabel>Deduct fees</InputLabel>
              <Tooltip message='If enabled, network fees are deducted proportionally from payout entries.' />
            </div>
            <div
              onClick={() => setDeductFees(!deductFees)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <Checkbox checked={deductFees} disabled={isLoading} />
              <span>Deduct fees from payouts</span>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 14 }}>
          <PrimaryButton
            isLoading={isLoading}
            disabled={!canCreate}
            style={{ width: '100%' }}
            onClick={async () => await createPayout()}>
            Create mass payout
          </PrimaryButton>
        </div>
      </Modal>
    }
  </Container>
})

export default CreateMassivePayout
